/**
 * zxcvbn-based password strength meter (#33).
 *
 * Informational only — does NOT block submission. The hard gates are:
 *   - server-side haveIBeenPwned plugin (rejects HIBP-known passwords)
 *   - server-side minPasswordLength (8) / maxPasswordLength (128)
 * Per NIST SP 800-63B, no forced uppercase/digit/symbol rules.
 *
 * zxcvbn ships ~400KB of wordlists. We dynamic-import it so the
 * wordlist lands in its own chunk that's only loaded on the auth pages,
 * not on every page of the SPA.
 */
import { useEffect, useState } from 'react';

interface ZxcvbnResult {
  score: 0 | 1 | 2 | 3 | 4;
  feedback: { warning?: string; suggestions: string[] };
}

interface PasswordStrengthMeterProps {
  password: string;
  /** Optional inputs that should reduce the score (email, name, etc. — guessable context). */
  userInputs?: string[];
}

const SCORE_LABELS: Record<number, { label: string; color: string }> = {
  0: { label: 'Very weak', color: 'var(--color-error-text)' },
  1: { label: 'Weak', color: 'var(--color-error-text)' },
  2: { label: 'Fair', color: 'var(--color-warning-text)' },
  3: { label: 'Strong', color: 'var(--color-success-text)' },
  4: { label: 'Very strong', color: 'var(--color-success-text)' },
};

export function PasswordStrengthMeter({ password, userInputs }: PasswordStrengthMeterProps) {
  const [result, setResult] = useState<ZxcvbnResult | null>(null);

  useEffect(() => {
    if (!password) {
      setResult(null);
      return;
    }
    let cancelled = false;
    // Dynamic import keeps zxcvbn out of the main bundle.
    import('zxcvbn').then(({ default: zxcvbn }) => {
      if (cancelled) return;
      setResult(zxcvbn(password, userInputs) as ZxcvbnResult);
    });
    return () => {
      cancelled = true;
    };
  }, [password, userInputs]);

  if (!result) return null;

  const { label, color } = SCORE_LABELS[result.score];
  const widthPct = ((result.score + 1) / 5) * 100;

  return (
    <div className="flex flex-col gap-1.5">
      <div
        className="h-1.5 w-full rounded-full overflow-hidden"
        style={{ background: 'var(--color-surface-muted)' }}
      >
        <div
          className="h-full rounded-full transition-all duration-200"
          style={{ width: `${widthPct}%`, backgroundColor: color }}
        />
      </div>
      <div className="flex justify-between text-xs gap-3">
        <span style={{ color, fontWeight: 500 }}>{label}</span>
        {result.feedback.warning && (
          <span style={{ color: 'var(--color-text-secondary)' }}>{result.feedback.warning}</span>
        )}
      </div>
      {result.feedback.suggestions.length > 0 && (
        <ul className="text-xs list-disc pl-4" style={{ color: 'var(--color-text-secondary)' }}>
          {result.feedback.suggestions.map((s, i) => (
            <li key={i}>{s}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
